<template>
  <!-- Админка не следует пресету скруглений витрины — см. комментарий в admin-kit.vue -->
  <div style="--app-radius-card: 1.5rem; --app-radius-media: 1rem; --app-radius-control: 9999px">
  <div class="min-h-screen flex flex-col lg:flex-row bg-gray-50 dark:bg-gray-900">

    <!-- Mobile Header -->
    <header class="lg:hidden bg-gray-900 text-white px-4 py-3 flex items-center justify-between shadow-md">
      <div class="flex items-center gap-2 min-w-0">
        <UIcon name="i-heroicons-building-storefront" class="w-5 h-5 text-orange-400 shrink-0" />
        <span class="text-base font-bold truncate">Админ-панель</span>
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <UiColorModeButton custom-class="!ml-0" :hide-label="true" />
        <button
          class="p-2 rounded-lg hover:bg-gray-700 transition-colors"
          @click="isSidebarOpen = !isSidebarOpen"
        >
          <UIcon name="i-heroicons-bars-3" class="w-5 h-5" />
        </button>
      </div>
    </header>

    <!-- Sidebar -->
    <aside
      :class="[
        'bg-gray-900 text-white flex flex-col transition-all duration-300 ease-in-out',
        'fixed inset-y-0 left-0 z-50 w-60 lg:translate-x-0 lg:z-40',
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        isCollapsed ? (isHovered ? 'lg:w-72' : 'lg:w-[4.25rem]') : 'lg:w-72',
        isRail ? 'admin-rail' : ''
      ]"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <!-- Logo / Brand + переключатель сворачивания -->
      <div
        class="px-3 py-4 border-b border-gray-700/60 hidden lg:flex items-center gap-2"
        :class="isRail ? 'justify-center' : 'justify-between'"
      >
        <div class="flex items-center gap-2.5 min-w-0" :class="{ 'lg:hidden': isRail }">
          <div class="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center shrink-0">
            <UIcon name="i-heroicons-building-storefront" class="w-5 h-5 text-white" />
          </div>
          <div class="min-w-0">
            <div class="text-sm font-bold text-white leading-tight truncate">Админ-панель</div>
            <div class="text-xs text-gray-400 truncate">Управление</div>
          </div>
          <UiColorModeButton custom-class="!ml-0" :hide-label="true" />
        </div>
        <button
          class="p-2 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-colors shrink-0"
          :title="isCollapsed ? 'Закрепить меню развёрнутым' : 'Свернуть меню до иконок'"
          @click="toggleCollapse"
        >
          <UIcon
            :name="isCollapsed ? 'i-heroicons-chevron-double-right' : 'i-heroicons-chevron-double-left'"
            class="w-5 h-5"
          />
        </button>
      </div>

      <!-- Mobile header in sidebar -->
      <div class="lg:hidden px-4 py-3 border-b border-gray-700/60 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div class="w-7 h-7 bg-orange-500 rounded-lg flex items-center justify-center">
            <UIcon name="i-heroicons-building-storefront" class="w-4 h-4 text-white" />
          </div>
          <span class="text-sm font-bold">Меню</span>
        </div>
        <button
          class="p-1.5 rounded-lg hover:bg-gray-700 transition-colors"
          @click="isSidebarOpen = false"
        >
          <UIcon name="i-heroicons-x-mark" class="w-5 h-5" />
        </button>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 px-3 py-3 space-y-1 overflow-y-auto">

        <!-- Главная -->
        <NuxtLink
          to="/administration"
          class="nav-link"
          active-class="nav-link--active"
          @click="isSidebarOpen = false"
        >
          <UIcon name="i-heroicons-home" class="w-4 h-4 shrink-0" />
          <span>Главная</span>
        </NuxtLink>

        <!-- ── БЛЮДА / КАТАЛОГ ── -->
        <AdminNavGroup
          title="Блюда"
          icon="i-heroicons-squares-2x2"
          storage-key="dishes"
          :default-open="true"
        >
          <NuxtLink
            to="/administration/products"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-squares-2x2" class="w-4 h-4 shrink-0" />
            <span>Все блюда</span>
            <UnassignedProductsBadge
              :auto-load="true"
              variant="warning"
              :show-icon="false"
              class="ml-auto"
            />
          </NuxtLink>

          <NuxtLink
            to="/administration/categories"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-folder" class="w-4 h-4 shrink-0" />
            <span>Категории</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/tags"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-tag" class="w-4 h-4 shrink-0" />
            <span>Теги</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/addon"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-plus-circle" class="w-4 h-4 shrink-0" />
            <span>Добавки</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/excludeIngredients"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-minus-circle" class="w-4 h-4 shrink-0" />
            <span>Отбавки</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/productGroup"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-rectangle-group" class="w-4 h-4 shrink-0" />
            <span>Группы продуктов</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/combos"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-gift" class="w-4 h-4 shrink-0" />
            <span>Комбо</span>
          </NuxtLink>
        </AdminNavGroup>

        <!-- ── МАРКЕТИНГ ── -->
        <AdminNavGroup
          title="Маркетинг"
          icon="i-heroicons-megaphone"
          storage-key="marketing"
          :default-open="false"
        >
          <NuxtLink
            to="/administration/retention"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-arrow-path" class="w-4 h-4 shrink-0" />
            <span>Retention</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/promocodes"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-ticket" class="w-4 h-4 shrink-0" />
            <span>Промокоды</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/referral"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-user-plus" class="w-4 h-4 shrink-0" />
            <span>Реферальная программа</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/news-slider"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-photo" class="w-4 h-4 shrink-0" />
            <span>Слайдер новостей</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/loyalty/settings"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-cog-6-tooth" class="w-4 h-4 shrink-0" />
            <span>Лояльность: настройки</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/loyalty/points"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-banknotes" class="w-4 h-4 shrink-0" />
            <span>Лояльность: баллы</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/loyalty/cleanup"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-fire" class="w-4 h-4 shrink-0" />
            <span>Лояльность: сгорание</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/loyalty/notifications"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-bell-alert" class="w-4 h-4 shrink-0" />
            <span>Лояльность: пуши</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/loyalty/abuse"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-shield-exclamation" class="w-4 h-4 shrink-0" />
            <span>Лояльность: лог абуза</span>
          </NuxtLink>
        </AdminNavGroup>

        <!-- ── КОММУНИКАЦИИ ── -->
        <AdminNavGroup
          title="Коммуникации"
          icon="i-heroicons-bell"
          storage-key="comms"
          :default-open="false"
        >
          <NuxtLink
            to="/administration/notifications"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-bell" class="w-4 h-4 shrink-0" />
            <span>Уведомления</span>
          </NuxtLink>
        </AdminNavGroup>

        <!-- ── ЗАВЕДЕНИЕ ── -->
        <AdminNavGroup
          title="Заведение"
          icon="i-heroicons-building-office"
          storage-key="venue"
          :default-open="false"
        >
          <NuxtLink
            to="/administration/workload-control"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-bolt" class="w-4 h-4 shrink-0" />
            <span>Управление загрузкой</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/orders"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-clipboard-document-list" class="w-4 h-4 shrink-0" />
            <span>Заказы</span>
          </NuxtLink>

          <NuxtLink
            to="/administration/settings"
            class="nav-link nav-link--child"
            active-class="nav-link--active"
            @click="isSidebarOpen = false"
          >
            <UIcon name="i-heroicons-cog-6-tooth" class="w-4 h-4 shrink-0" />
            <span>Настройки</span>
          </NuxtLink>

        </AdminNavGroup>

      </nav>

      <!-- Footer: logout -->
      <div class="px-3 py-3 border-t border-gray-700/60">
        <button
          class="nav-logout w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
          :title="isRail ? 'Выйти из AdminPanel' : undefined"
          @click="() => (storeAuth as any).adminlogout()"
        >
          <UIcon name="i-heroicons-arrow-right-on-rectangle" class="w-4 h-4 shrink-0" />
          <span>Выйти из AdminPanel</span>
        </button>
      </div>
    </aside>

    <!-- Overlay (mobile) -->
    <Transition name="overlay">
      <div
        v-if="isSidebarOpen"
        class="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
        @click="isSidebarOpen = false"
      />
    </Transition>

    <!-- Main Content -->
    <main
      class="flex-1 bg-gray-50 dark:bg-gray-900 min-h-screen overflow-x-hidden transition-[padding] duration-300"
      :class="isCollapsed ? 'lg:pl-[4.25rem]' : 'lg:pl-60'"
    >
      <div class="p-3 lg:p-6">
        <slot />
      </div>
    </main>
  </div>

  <DynamicModal v-if="storeModal.isOpen" @close="storeModal.close" />
  </div>
</template>

<script setup lang="ts">
import { useModalStore } from '@shared/model'
import { useAuthStore } from '~/features/auth'

const storeAuth = useAuthStore()
const storeModal = useModalStore()

const isSidebarOpen = ref(false)

// Сворачивание сайдбара в «rail» (только иконки) на десктопе.
// isCollapsed — закреплённое состояние (кнопкой), isHovered — временное раскрытие по наведению.
const COLLAPSE_KEY = 'admin-sidebar-collapsed'
const isCollapsed = ref(false)
const isHovered = ref(false)
const isRail = computed(() => isCollapsed.value && !isHovered.value)

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
  if (process.client) {
    localStorage.setItem(COLLAPSE_KEY, String(isCollapsed.value))
  }
}

onMounted(() => {
  if (process.client) {
    const saved = localStorage.getItem(COLLAPSE_KEY)
    if (saved !== null) {
      isCollapsed.value = saved === 'true'
    }
  }
  const handleResize = () => {
    if (window.innerWidth >= 1024) {
      isSidebarOpen.value = false
    }
  }
  window.addEventListener('resize', handleResize)
  onUnmounted(() => window.removeEventListener('resize', handleResize))
})
</script>

<style scoped>
.nav-link {
  @apply
    flex items-center gap-2.5
    px-3 py-2 rounded-lg
    text-sm text-gray-400
    hover:bg-gray-700/60 hover:text-white
    transition-colors duration-150
    w-full;
}

.nav-link--child {
  @apply text-gray-400 text-sm py-1.5;
}

/* ── Rail-режим: только иконки (десктоп) ── */
@media (min-width: 1024px) {
  .admin-rail .nav-link,
  .admin-rail .nav-logout {
    @apply justify-center px-0 gap-0;
  }

  /* Прячем текстовые подписи и бейджи, оставляя только иконку (.shrink-0) */
  .admin-rail .nav-link > span:not([class*='shrink-0']):not([class*='ml-auto']),
  .admin-rail .nav-link .ml-auto,
  .admin-rail .nav-logout > span:not([class*='shrink-0']) {
    @apply hidden;
  }

  /* Группы навигации: показываем только иконку группы (классы из дочернего AdminNavGroup, через :deep) */
  /* noinspection CssUnusedSymbol */
  .admin-rail :deep(.nav-group__header) {
    @apply justify-center px-0;
  }
  /* noinspection CssUnusedSymbol */
  .admin-rail :deep(.nav-group__title),
  .admin-rail :deep(.nav-group__chevron),
  .admin-rail :deep(.nav-group__items) {
    @apply hidden;
  }
}
</style>

<style>
/* nav-link--active навешивается через active-class у NuxtLink; overlay-* — классы <Transition name="overlay">.
   IDE не распознаёт эти привязки, поэтому подавляем ложные "unused selector". */
/* noinspection CssUnusedSymbol */
.nav-link--active {
  @apply bg-orange-500/20 text-orange-300 font-medium;
}

/* noinspection CssUnusedSymbol */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.2s ease;
}
/* noinspection CssUnusedSymbol */
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}
</style>
